/*
  Tabs swap the products below without touching the browser URL: the page stays
  on its own collection, and only the grid is replaced.

  The tab's collection is fetched through the Section Rendering API. That fetch
  carries view=tabs-filter so the target collection renders this section even
  when it has a different template assigned — it is a request parameter only and
  never reaches the address bar.

  Tabs are real links, so without JS a click still navigates.
*/
(function () {
  var root = document.querySelector('.collection-tabs[data-section-id]');
  if (!root) return;

  var request = null;

  // Resolved on use, never cached: the theme's own filter ajax replaces
  // #CollectionAjaxContent, which throws away the toolbar, the drawer and the
  // product wrapper. Held references would survive as detached nodes and every
  // later swap would write into nothing. Only `root` outlives that.
  function productsEl() {
    return root.querySelector('#CollectionTabsProducts');
  }

  function tabEls() {
    return root.querySelectorAll('[data-collection-tab]');
  }

  function setActive(tab) {
    Array.prototype.forEach.call(tabEls(), function (item) {
      var isActive = item === tab;
      item.classList.toggle('btn--tertiary-active', isActive);

      if (isActive) item.setAttribute('aria-current', 'page');
      else item.removeAttribute('aria-current');
    });

    // A tab swap only replaces #CollectionTabsProducts, so the drawer's Tabs
    // radios are not re-rendered with it. Keep them in step by hand. Assigning
    // .checked fires no change event, so this cannot loop back into load().
    //
    // Paired by position: both lists walk the same product_tab blocks in the
    // same order, so the index is exact. Matching on the collection URL instead
    // would silently fail if the two ever printed it differently.
    if (!tab) return;
    var active = tab.getAttribute('data-collection-tab');
    root.querySelectorAll('[data-collection-tab-radio]').forEach(function (radio) {
      radio.checked = radio.getAttribute('data-collection-tab-radio') === active;
    });
  }

  // Banners live as <template>s beside the grid, rendered once in the page's own
  // collection context. The grid is replaced wholesale on every tab swap and on
  // the theme's filter ajax, taking any injected clones with it, so they are
  // re-inserted after each render rather than tracked.
  function applyBanners() {
    var grid = root.querySelector('[data-collection-tabs-products]');
    var templates = root.querySelectorAll('[data-collection-banner]');
    if (!grid || !templates.length) return;

    // Idempotent: clear any banners already in the grid first. Normally the grid
    // has just been replaced and holds none, but collection:reloaded can fire
    // without a replacement, and injecting again would double every banner.
    Array.prototype.forEach.call(grid.querySelectorAll('.collection-banner'), function (old) {
      old.remove();
    });

    // Captured before inserting anything: positions count product cards only,
    // so a banner must not shift the card a later banner is measured against.
    var cards = grid.querySelectorAll('.grid-product');

    Array.prototype.forEach.call(templates, function (template) {
      var position = parseInt(template.getAttribute('data-position'), 10);
      var card = cards[position - 1];
      if (!card) return;

      card.insertAdjacentHTML('afterend', template.innerHTML);
    });
  }

  // Category rail: every Category block's button is in the page, tagged with
  // the tab that owns it. Only the active tab's buttons show.
  function showRail(index) {
    var rail = root.querySelector('[data-collection-rail]');
    if (!rail) return null;

    var first = null;
    rail.querySelectorAll('[data-collection-category]').forEach(function (item) {
      var mine = item.getAttribute('data-tab') === String(index);
      item.hidden = !mine;
      if (mine && !first) first = item;
    });
    rail.hidden = !first;
    return first;
  }

  function setCategory(current) {
    root.querySelectorAll('[data-collection-category]').forEach(function (item) {
      var isActive = item === current;
      item.classList.toggle('is-active', isActive);
      if (isActive) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });
  }

  // A tab with categories opens on its first category, as the bundle builder does.
  function openTab(tab, href) {
    var first = tab && showRail(tab.getAttribute('data-collection-tab'));
    if (first) {
      setCategory(first);
      href = first.href;
    }
    load(href, tab);
  }

  function tabAt(index) {
    return root.querySelector('[data-collection-tab="' + index + '"]');
  }

  function load(href, tab) {
    if (request) request.abort();
    request = new AbortController();
    root.setAttribute('aria-busy', 'true');
    if (tab) setActive(tab);

    var url = new URL(href, window.location.origin);

    // Carry the page's own params (sort_by, filter.*) onto the tab's URL.
    // Without this the fetch returns the tab collection in its default order,
    // silently undoing whatever sort or filter the visitor just chose.
    new URL(window.location.href).searchParams.forEach(function (value, key) {
      if (key === 'view' || key === 'section_id' || key === 'page') return;
      if (url.searchParams.has(key)) return;
      url.searchParams.append(key, value);
    });

    url.searchParams.set('view', 'tabs-filter');
    url.searchParams.set('section_id', root.dataset.sectionId);

    var target = productsEl();
    if (!target) return;

    fetch(url.toString(), { signal: request.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Collection request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var next = new DOMParser()
          .parseFromString(html, 'text/html')
          .getElementById('CollectionTabsProducts');

        if (!next) throw new Error('Products were not found in the response');

        target.innerHTML = next.innerHTML;
        applyBanners();

        if (window.theme && theme.reinitProductGridItem) theme.reinitProductGridItem();
      })
      .catch(function (error) {
        if (error.name !== 'AbortError') console.error(error);
      })
      .then(function () {
        root.removeAttribute('aria-busy');
      });
  }

  root.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-collection-tab]');
    var category = event.target.closest('[data-collection-category]');
    var page = event.target.closest('#CollectionTabsProducts .pagination a');
    var link = tab || category || page;

    if (!link) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    if (link.hasAttribute('aria-current')) return;

    if (tab) return openTab(tab, tab.href);

    if (category) {
      setCategory(category);
      // A switch made deep in one category would land deep in the next. Once
      // the rail is pinned, the catalog's top has scrolled above it: bring the
      // catalog back to that line (its scroll-margin-top in collection-tabs.css).
      var catalog = root.querySelector('.collection-tabs__catalog');
      var rail = root.querySelector('[data-collection-rail]');
      if (catalog && rail && catalog.getBoundingClientRect().top < rail.getBoundingClientRect().top - 1) {
        catalog.scrollIntoView({ block: 'start' });
      }
    }

    load(link.href, null);
  });

  // Sort radios replace the theme's #SortBy select, so its handler no longer
  // applies. Same outcome as that handler: set sort_by, drop the page number.
  root.addEventListener('change', function (event) {
    // Tabs radio: same in-place swap as clicking the tab button.
    var tabRadio = event.target.closest('[data-collection-tab-radio]');
    if (tabRadio) {
      openTab(tabAt(tabRadio.getAttribute('data-collection-tab-radio')), tabRadio.value);
      return;
    }

    var sort = event.target.closest('[data-collection-sort] input[type="radio"]');
    if (!sort) return;

    var url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  });

  // The theme's filter ajax rebuilds the grid without going through load().
  document.addEventListener('collection:reloaded', applyBanners);

  applyBanners();

  // The section renders the opening grid (default tab or first category)
  // itself. It names it here only when the page URL sorts or filters, which
  // Liquid applies to the page's own collection only, so it is fetched instead.
  var opening = root.getAttribute('data-collection-tabs-default');
  if (opening) load(opening, root.querySelector('[data-collection-tab][aria-current="page"]'));
})();
